import { createContext, useContext } from 'react'

import { SUNSHINE_ORG, STAFF_BY_ROLE } from './mockData'

// Mirrors the live portal's OrganizationRole + IProviderSession shapes so the
// ported screens read session state through the same hook signature they use in
// production. The only difference is that role is switchable from the preview
// chrome instead of coming from GET /provider/me.
export type OrganizationRole = 'admin' | 'coordinator' | 'viewer'

export interface IProviderSession {
    user: {
        name: string | null
        email: string
    }
    organization: {
        name: string
        displayName: string
        contactName: string | null
        contactEmail: string | null
        seatQuota: number
        type: string
    }
    role: OrganizationRole
}

interface PreviewSessionValue {
    role: OrganizationRole
    setRole: (role: OrganizationRole) => void
    session: IProviderSession
}

const PreviewSessionContext = createContext<PreviewSessionValue | null>(null)

// Build the active session from the chosen role. The "current user" identity
// follows the role so role-aware screens (e.g. the self-guard on the team
// member detail) behave the way they would for a real signed-in staffer.
export function buildSession(role: OrganizationRole): IProviderSession {
    return {
        user: STAFF_BY_ROLE[role],
        organization: SUNSHINE_ORG,
        role,
    }
}

export const PreviewSessionProvider = PreviewSessionContext.Provider

// Drop-in replacement for the live useProviderSession(). Returns null outside a
// provider, matching the live hook so the ported screens need no changes.
export function useProviderSession(): IProviderSession | null {
    return useContext(PreviewSessionContext)?.session ?? null
}

// For the role switcher in the preview chrome.
export function usePreviewRole(): { role: OrganizationRole; setRole: (role: OrganizationRole) => void } {
    const ctx = useContext(PreviewSessionContext)
    if (!ctx) return { role: 'admin', setRole: () => {} }
    return { role: ctx.role, setRole: ctx.setRole }
}
